/**
 * TokenPurchaseDialog — modal showing 3 token pack cards with value framing.
 * On select, creates a Stripe Checkout session and opens in new tab.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@/store/accountStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { capabilities } from '@/capabilities.ts';
import { requestCreditPurchase, setPendingPackDetails } from '@/bridge/embeddedBridge.ts';
import {
  TOKEN_PACKS,
  LOW_BALANCE_THRESHOLD,
  getOutputEstimates,
  FALLBACK_CATALOG,
} from '@/ai/tokenCosts.ts';
import { createCheckoutSession, getPurchaseHistory } from '@/api/stripeApi.ts';
import type { PurchaseRecord } from '@/api/stripeApi.ts';
import styles from './TokenPurchaseDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onPurchase?: (packId: string) => Promise<void>;
}

const SAVINGS_KEYS: Record<string, string> = {
  pack_100: 'savingsTopOff',
  pack_400: 'savingsSave25',
  pack_1200: 'savingsSave33',
};

function formatPrice(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

export function TokenPurchaseDialog({ open, onClose, onPurchase }: Props) {
  const { t } = useTranslation('account');
  const accountBalance = useAccountStore(s => s.accountBalance);
  const catalog = useAiStore(s => s.aiModelCatalog);
  const effectiveCatalog = catalog.length > 0 ? catalog : FALLBACK_CATALOG;
  const [loadingPackId, setLoadingPackId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Purchase history (lazy-loaded)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<PurchaseRecord[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const isEmbedded = capabilities.bridge;

  const handleSelectPack = useCallback(async (packId: string) => {
    setError('');
    setLoadingPackId(packId);
    try {
      if (onPurchase) {
        // Custom purchase handler (used by CreditPacksPage)
        const oldBalance = accountBalance?.available ?? 0;
        const pack = TOKEN_PACKS.find(p => p.id === packId);
        await onPurchase(packId);
        setLoadingPackId(null);
        if (pack) {
          useAccountStore.getState().showIapSuccess({
            packLabel: pack.label,
            creditsAdded: pack.tokens,
            oldBalance,
            newBalance: oldBalance + pack.tokens,
          });
        }
        onClose();
      } else if (isEmbedded) {
        // Capture balance before purchase for success dialog
        const oldBalance = accountBalance?.available ?? 0;
        const pack = TOKEN_PACKS.find(p => p.id === packId);
        // Stash pack metadata so creditPurchaseResult handler can fire pack_purchased
        if (pack) {
          setPendingPackDetails({ pack_id: pack.id, credits: pack.tokens, price_cents: pack.priceCents });
        }
        // Embedded: route through Apple IAP via Unity bridge
        await requestCreditPurchase(packId);
        setLoadingPackId(null);
        if (pack) {
          // Show animated success dialog
          useAccountStore.getState().showIapSuccess({
            packLabel: pack.label,
            creditsAdded: pack.tokens,
            oldBalance,
            newBalance: oldBalance + pack.tokens,
          });
        }
        onClose();
      } else {
        // Standalone/web: existing Stripe Checkout flow
        const checkoutUrl = await createCheckoutSession(packId);
        window.open(checkoutUrl, '_blank');
        setLoadingPackId(null);
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorPurchaseFailed'));
      setLoadingPackId(null);
    }
  }, [onClose, onPurchase, isEmbedded, t, accountBalance]);

  const handleToggleHistory = useCallback(async () => {
    if (!historyOpen && history === null) {
      setHistoryLoading(true);
      try {
        const records = await getPurchaseHistory();
        setHistory(records);
      } catch {
        setHistory([]);
      }
      setHistoryLoading(false);
    }
    setHistoryOpen(prev => !prev);
  }, [historyOpen, history]);

  if (!open) return null;

  const isLowBalance = accountBalance && !accountBalance.isBeta && accountBalance.available < LOW_BALANCE_THRESHOLD;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>{t('addCreditsTitle')}</span>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div className={styles.body}>
          {/* Low-balance warning */}
          {isLowBalance && (
            <div className={styles.lowBalanceWarning}>
              {t('lowBalanceWarning')}{' '}
              <span className={styles.lowBalanceCount}>
                {t('lowBalanceRemaining', { count: accountBalance.available })}
              </span>{' '}
              {t('remainingLabel')}
            </div>
          )}

          {/* Pack cards */}
          <div className={styles.packGrid}>
            {TOKEN_PACKS.map(pack => {
              const isHighlighted = pack.id === 'pack_400';
              const savingsKey = SAVINGS_KEYS[pack.id];
              const estimates = getOutputEstimates(pack.tokens, effectiveCatalog);
              const isLoading = loadingPackId === pack.id;
              const isDisabled = loadingPackId !== null && !isLoading;

              return (
                <div
                  key={pack.id}
                  className={`${styles.packCard} ${isHighlighted ? styles.packCardHighlighted : ''} ${isDisabled ? styles.packCardDisabled : ''}`}
                  onClick={() => !loadingPackId && handleSelectPack(pack.id)}
                >
                  {savingsKey && <div className={styles.savingsPill}>{t(savingsKey)}</div>}
                  <div className={styles.packLabel}>{pack.label}</div>
                  <div className={styles.packTokens}>
                    {pack.tokens.toLocaleString()}
                    <span className={styles.packTokenUnit}> {t('packTokenUnit')}</span>
                  </div>
                  <div className={styles.packPrice}>
                    {formatPrice(pack.priceCents)}
                  </div>
                  <div className={styles.packEstimate}>
                    {t('packEstimate', { images: estimates.enhancedImages, videos: estimates.videos })}
                  </div>
                  <button
                    className={`${styles.packCta} ${isHighlighted ? styles.packCtaPrimary : ''}`}
                    disabled={loadingPackId !== null}
                    onClick={e => { e.stopPropagation(); if (!loadingPackId) handleSelectPack(pack.id); }}
                  >
                    {isLoading ? (isEmbedded ? t('processingButton') : t('openingButton')) : t('buyPackButton', { label: pack.label })}
                  </button>
                </div>
              );
            })}
          </div>

          {error && <div className={styles.error}>{error}</div>}

          {/* Purchase history */}
          <button className={styles.historyToggle} onClick={handleToggleHistory}>
            {historyOpen ? t('hidePurchaseHistory') : t('purchaseHistoryToggle')}
          </button>
          {historyOpen && (
            <div className={styles.historySection}>
              {historyLoading ? (
                <div className={styles.historyRow}>{t('purchaseHistoryLoading')}</div>
              ) : history && history.length > 0 ? (
                history.map(record => (
                  <div key={record.id} className={styles.historyRow}>
                    <span className={styles.historyDate}>
                      {new Date(record.created_at).toLocaleDateString()}
                    </span>
                    <span className={styles.historyTokens}>
                      +{record.tokens_purchased.toLocaleString()} {t('packTokenUnit')}
                    </span>
                    <span className={styles.historyAmount}>
                      {formatPrice(record.amount_cents)}
                    </span>
                  </div>
                ))
              ) : (
                <div className={styles.historyRow}>{t('noPurchasesYet')}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
